/** Plain-English dates and times for cards and lists. */

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** "just now", "5 minutes ago", "yesterday at 14:05", "3 Oct at 09:12", "3 Oct 2025". */
export function timeAgo(iso: string | number, now: number = Date.now()): string {
  const t = typeof iso === 'number' ? iso : Date.parse(iso);
  if (!Number.isFinite(t)) return '';
  const diff = now - t;
  if (diff < 45_000) return 'just now';
  if (diff < HOUR) {
    const m = Math.max(1, Math.round(diff / MINUTE));
    return m === 1 ? '1 minute ago' : `${m} minutes ago`;
  }
  const d = new Date(t);
  const today = new Date(now);
  const time = d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  if (sameDay(d, today)) {
    const h = Math.round(diff / HOUR);
    return diff < 6 * HOUR ? (h === 1 ? '1 hour ago' : `${h} hours ago`) : `today at ${time}`;
  }
  const yesterday = new Date(now - DAY);
  if (sameDay(d, yesterday)) return `yesterday at ${time}`;
  const sameYear = d.getFullYear() === today.getFullYear();
  const date = d.toLocaleDateString(undefined, sameYear ? { day: 'numeric', month: 'short' } : { day: 'numeric', month: 'short', year: 'numeric' });
  return sameYear && diff < 30 * DAY ? `${date} at ${time}` : date;
}

/** "Today", "Yesterday", or a date like "Monday 13 October": headings for grouped lists. */
export function dayHeading(iso: string, now: number = Date.now()): string {
  const d = new Date(iso);
  if (sameDay(d, new Date(now))) return 'Today';
  if (sameDay(d, new Date(now - DAY))) return 'Yesterday';
  const sameYear = d.getFullYear() === new Date(now).getFullYear();
  return d.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long', ...(sameYear ? {} : { year: 'numeric' }) });
}

/** Time of day, "14:05". */
export function clock(iso: string | number): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

/** Whole days left before a deleted plan is removed for good (30 days after deletion). */
export function daysLeftInTrash(deletedAt: string, now: number = Date.now()): number {
  const left = Date.parse(deletedAt) + 30 * DAY - now;
  return Math.max(0, Math.ceil(left / DAY));
}

function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

/** "My zoo 15 Oct": the default name for a new plan. */
export function defaultPlanName(now: number = Date.now()): string {
  return `My zoo ${new Date(now).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}`;
}
